import * as Yup from "utils/yup"
import { NUMERIC_SAFE_MAX } from "utils/numericValidation"

export const PLAN_NAME_MAX_LENGTH = 100
export const PLAN_DESCRIPTION_MAX_LENGTH = 2000
export const PLAN_NUMERIC_LIMIT_MAX = NUMERIC_SAFE_MAX

const planNamePattern = /^[a-zA-Z0-9\s\-_'&.]+$/
const planNameUnsafeContentPattern = /<|>|javascript:|on\w+\s*=/i

export const containsUnsafePlanNameContent = (value = "") =>
  planNameUnsafeContentPattern.test(String(value))

export const buildPlanNameSchema = t =>
  Yup.string()
    .trim()
    .required(t("Plan name is required"))
    .min(2, t("Plan name must be at least 2 characters"))
    .max(
      PLAN_NAME_MAX_LENGTH,
      t("Plan name cannot exceed {{max}} characters", { max: PLAN_NAME_MAX_LENGTH })
    )
    .test(
      "no-unsafe-content",
      t("Plan name cannot contain HTML or script content."),
      value => !containsUnsafePlanNameContent(value)
    )
    .test(
      "no-parentheses",
      t("Parentheses are not allowed in this field"),
      value => !value || !/[()]/.test(value)
    )
    .matches(
      planNamePattern,
      t("Plan name may only contain letters, numbers, spaces, and - _ ' & .")
    )
    .test(
      "plan-name-has-letter",
      t("Plan name must include at least one letter"),
      value => /[a-zA-Z]/.test(value || "")
    )

export const buildPlanDescriptionSchema = t =>
  Yup.string()
    .trim()
    .max(
      PLAN_DESCRIPTION_MAX_LENGTH,
      t("Description cannot exceed {{max}} characters", {
        max: PLAN_DESCRIPTION_MAX_LENGTH,
      })
    )
    .test(
      "no-unsafe-content",
      t("Description cannot contain HTML or script content."),
      value => !value || !containsUnsafePlanNameContent(value)
    )

export const buildPlanNumericLimitSchema = (t, fieldLabel) =>
  Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? 0
        : value
    )
    .typeError(t("{{field}} must be a number", { field: fieldLabel }))
    .integer(t("{{field}} must be a whole number", { field: fieldLabel }))
    .min(0, t("{{field}} must be 0 or greater", { field: fieldLabel }))
    .max(
      NUMERIC_SAFE_MAX,
      t("{{field}} exceeds the maximum allowed value (max {{max}}).", {
        field: fieldLabel,
        max: NUMERIC_SAFE_MAX,
      })
    )

const isPlanNameApiMessage = message => {
  const normalized = String(message || "").toLowerCase()
  return (
    normalized.includes("plan name") ||
    normalized.includes("plan with this name") ||
    normalized.includes("بهذا الاسم") ||
    normalized.includes("اسم الخطة")
  )
}

const isPlanModulesApiMessage = message => {
  const normalized = String(message || "").toLowerCase()
  return (
    (normalized.includes("module") && normalized.includes("select")) ||
    normalized.includes("وحدة") ||
    normalized.includes("تحديد وحدة")
  )
}

const isMaxUsersApiMessage = message => {
  const normalized = String(message || "").toLowerCase()
  return (
    normalized.includes("max users") ||
    normalized.includes("maxadminusers") ||
    normalized.includes("للمستخدمين")
  )
}

const isMaxBranchesApiMessage = message => {
  const normalized = String(message || "").toLowerCase()
  return (
    normalized.includes("max branches") ||
    normalized.includes("maxbranches") ||
    normalized.includes("للفروع")
  )
}

export const mapPlanApiErrorToField = (response, t) => {
  const message =
    response?.msg ||
    response?.message ||
    response?.error ||
    t("Failed to save plan. Please try again.")

  const isSafeNumberMessage = String(message || "")
    .toLowerCase()
    .includes("safe number")

  if (isPlanNameApiMessage(message)) {
    return { field: "name", message }
  }
  if (isPlanModulesApiMessage(message)) {
    return { field: "modules", message }
  }
  if (isMaxUsersApiMessage(message)) {
    return {
      field: "maxAdminUsers",
      message: isSafeNumberMessage
        ? t("{{field}} exceeds the maximum allowed value (max {{max}}).", {
            field: t("Max Users"),
            max: NUMERIC_SAFE_MAX,
          })
        : message,
    }
  }
  if (isMaxBranchesApiMessage(message)) {
    return {
      field: "maxBranches",
      message: isSafeNumberMessage
        ? t("{{field}} exceeds the maximum allowed value (max {{max}}).", {
            field: t("Max Branches"),
            max: NUMERIC_SAFE_MAX,
          })
        : message,
    }
  }
  return { field: null, message }
}

export const applyPlanApiError = (
  response,
  { formik, dispatch, showAlert, t, onModulesError } = {}
) => {
  const { field, message } = mapPlanApiErrorToField(response, t)
  const translatedMessage = t(message, { defaultValue: message })

  if (field === "modules" && typeof onModulesError === "function") {
    onModulesError(translatedMessage)
  } else if (field && formik) {
    formik.setFieldError(field, translatedMessage)
    formik.setFieldTouched(field, true, false)
  }

  dispatch(
    showAlert({
      type: "error",
      text: translatedMessage,
    })
  )
}

export const isPlanActionSuccess = actionResult =>
  Boolean(actionResult?.payload?.status)
